import type { RoundEvent } from "@slot/client-engine";
import { ReelGridPresenter } from "./ReelGridPresenter";
import type { ReelSpinMechanic } from "./ReelSpinMechanic";
import type { TumbleMechanic } from "./TumbleMechanic";
import type { ReelTumbleTuning } from "./ReelTumbleTuning";
import type { GridCodec, Log, PreparedDropPlan, PresentationBarrier } from "@slot/presentation";
import type { BaseGridPresenter } from "@slot/mechanics-core";

/**
 * The reel adapter plus the tumble: a removal mask is remembered, the next collapse-like frame is
 * prepared as a tumble plan against it and played on present; anything else is a reel frame,
 * delegated to the plain `ReelGridPresenter` this one wraps.
 */
export class TumbleReelGridPresenter implements BaseGridPresenter {
  private readonly reels: ReelGridPresenter;
  private readonly reel: ReelSpinMechanic;
  private readonly tumble: TumbleMechanic;
  private readonly tumbleCodec: GridCodec;
  private readonly tumbleTuning: ReelTumbleTuning;
  private readonly tumbleLog: (message: string) => void;
  private lastRemovalMask: string | null = null;
  private pendingTumblePlan: PreparedDropPlan | null = null;
  private pendingTumbleKey: string | null = null;

  public constructor(input: {
    reel: ReelSpinMechanic;
    tumble: TumbleMechanic;
    gridCodec: GridCodec;
    presentationBarrier: PresentationBarrier;
    tumbleTuning: ReelTumbleTuning;
    log?: Log;
  }) {
    this.reels = new ReelGridPresenter({ reel: input.reel, gridCodec: input.gridCodec, presentationBarrier: input.presentationBarrier, log: input.log });
    this.reel = input.reel;
    this.tumble = input.tumble;
    this.tumbleCodec = input.gridCodec;
    this.tumbleTuning = input.tumbleTuning;
    this.tumbleLog = input.log ?? (() => {});
  }

  /** Paints a grid without motion (boot, recovery); no tumble or removal mask is left pending. */
  public async paintGrid(encodedGridSnapshot: string): Promise<void> {
    await this.reels.paintGrid(encodedGridSnapshot);
    this.pendingTumblePlan = null;
    this.pendingTumbleKey = null;
    this.lastRemovalMask = null;
  }

  public async removeSymbolsByMask(positionMask: string): Promise<void> {
    this.lastRemovalMask = positionMask;
    await this.tumble.removeByMask(positionMask, {
      shrinkSeconds: this.tumbleTuning.removal.shrinkSeconds,
      columnStaggerSeconds: this.tumbleTuning.removal.columnStaggerSeconds,
      withinColumnStaggerSeconds: this.tumbleTuning.removal.withinColumnStaggerSeconds,
    });
  }

  public async prepareEventFrame(event: RoundEvent): Promise<void> {
    if (!this.isCollapseLike(event.eventTypeName)) {
      await this.reels.prepareEventFrame(event);
      return;
    }
    const key = this.computeKey(event);
    const encoded = event.eventContent?.encodedGridSnapshot;
    if (!encoded || !this.lastRemovalMask) {
      this.tumbleLog(`[TumbleReelGridPresenter] collapse ${event.eventTypeName} without ${!encoded ? "snapshot" : "removal mask"} — instant fallback`);
      this.pendingTumblePlan = null;
      this.pendingTumbleKey = key;
      return;
    }
    const nextModel = this.tumbleCodec.decode(encoded);
    const t = this.tumbleTuning.tumble;
    this.pendingTumblePlan = this.tumble.prepareTumbleToModel({
      removedPositionMask: this.lastRemovalMask,
      nextModel,
      fallDurationSeconds: t.fallDurationSeconds,
      spawnDurationSeconds: t.spawnDurationSeconds,
      columnStaggerSeconds: t.columnStaggerSeconds,
      withinColumnStaggerSeconds: t.withinColumnStaggerSeconds,
      strictSymbolConsistencyCheck: true,
    });
    this.pendingTumbleKey = key;
  }

  public async presentEventFrame(event: RoundEvent): Promise<void> {
    if (!this.isCollapseLike(event.eventTypeName)) {
      await this.reels.presentEventFrame(event);
      return;
    }
    const key = this.computeKey(event);
    if (this.pendingTumbleKey !== key) {
      await this.prepareEventFrame(event);
    }
    const plan = this.pendingTumblePlan;
    this.pendingTumblePlan = null;
    this.pendingTumbleKey = null;
    this.lastRemovalMask = null;
    if (plan) {
      const t = this.tumbleTuning.tumble;
      await this.tumble.animatePreparedTumble(plan, {
        enableOvershoot: t.overshoot.enable,
        overshootPixels: t.overshoot.pixels,
        overshootPhaseRatio: t.overshoot.phaseRatio,
        overshootSettleEasing: t.overshoot.settleEasing,
        dropSpeedPixelsPerSecond: t.dropSpeedPixelsPerSecond,
        minDropDurationSeconds: t.minDropDurationSeconds,
        maxDropDurationSeconds: t.maxDropDurationSeconds,
      });
      return;
    }
    const encoded = event.eventContent?.encodedGridSnapshot;
    if (encoded) {
      this.reel.renderModel(this.tumbleCodec.decode(encoded));
    }
  }

  private isCollapseLike(name: string): boolean {
    return name === "collapse" || name.endsWith("_collapse") || name.includes("collapse");
  }

  /** The round first: an event's number is unique only within its round. */
  private computeKey(event: RoundEvent): string {
    return `${event.roundIdentifier}:${event.eventTypeName}:${event.eventSequenceNumber ?? ""}:${event.eventContent?.encodedGridSnapshot ?? ""}`;
  }
}
